import { useState, useCallback } from "react";
import { Box, useToast } from "@chakra-ui/react";
import {
  downloadCommissionsB2C,
  downloadCommissionsB2B,
  getCommissionsB2CPaginated,
  getCommissionsB2BPaginated,
} from "@/services/financial-service/commission.service";
import { downloadKamReport, getKamPaginated } from "@/services/financial-service/kam.service";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { formatPhone } from "@/utils/formatPhone";
import { AutocompleteOption } from "../FinancialShared/DebouncedAutocompleteInput";
import { CommissionFilters as ICommissionFilters } from "@/models/commission-report.model";

// Modular Components
import CommissionFilters from "./CommissionFilters";
import CommissionTable from "./CommissionTable";
import {
  INITIAL_FILTERS,
  B2C_COLUMNS,
  KAM_COLUMNS,
  B2B_COLUMNS_BASE,
  B2B_COLUMNS_COMMON,
  B2B_BLUEPAY_COLUMNS,
  DATE_KEYS,
  CURRENCY_KEYS,
  DOCUMENT_KEYS,
  PHONE_KEYS,
  startOfMonth,
  endOfMonth,
  B2B_KAMINO_COLUMNS,
} from "./constants";

interface CommissionTabProps {
  backgroundStyle: any;
}

export default function CommissionTab({ backgroundStyle }: CommissionTabProps) {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([startOfMonth, endOfMonth]);
  const [filters, setFilters] = useState<ICommissionFilters>(INITIAL_FILTERS);
  const [data, setData] = useState<any[]>([]);
  const [meta, setMeta] = useState({ totalPages: 0, currentPage: 1, itemsPerPage: 5 });
  const [pageSize, setPageSize] = useState(5);
  const [isQuerying, setIsQuerying] = useState(false);
  const [isDownloadingB2C, setIsDownloadingB2C] = useState(false);
  const [isDownloadingB2B, setIsDownloadingB2B] = useState(false);
  const [isDownloadingKam, setIsDownloadingKam] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [vision, setVision] = useState<string>("");
  const [selectedCorban, setSelectedCorban] = useState<AutocompleteOption | null>(null);
  const [selectedPromotora, setSelectedPromotora] = useState<AutocompleteOption | null>(null);
  const [selectedGerente, setSelectedGerente] = useState<AutocompleteOption | null>(null);
  const [selectedVendedor, setSelectedVendedor] = useState<AutocompleteOption | null>(null);

  const toast = useToast();

  const currentColumns =
    filters.type === "b2b"
      ? vision === "kamino"
        ? [...B2B_COLUMNS_COMMON, ...B2B_KAMINO_COLUMNS]
        : vision === "bluepay"
        ? B2B_BLUEPAY_COLUMNS
        : B2B_COLUMNS_BASE
      : filters.type === "kam"
      ? KAM_COLUMNS
      : B2C_COLUMNS;

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    setDateRange(dates);
    setFilters((prev) => ({
      ...prev,
      startDate: dates[0]?.toISOString() ?? "",
      endDate: dates[1]?.toISOString() ?? "",
    }));
  };

  const handleQuery = async (page = 1, limit = pageSize) => {
    if (!filters.startDate || !filters.endDate) {
      toast({ title: "Selecione o intervalo de datas", status: "warning", duration: 3000 });
      return;
    }
    if ((filters.type === "b2b" || filters.type === "kam") && !vision) {
      toast({
        title: "Atenção",
        description: filters.type === "b2b"
          ? "Selecione a bancarizadora do relatório para B2B."
          : "Selecione a visão (B2B, B2C ou Ambos) para KAM.",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    setIsQuerying(true);
    try {
      let result;
      if (filters.type === "b2c") {
        result = await getCommissionsB2CPaginated(
          filters.startDate,
          filters.endDate,
          page,
          limit,
          {
            corbanId: selectedCorban?.value !== undefined ? Number(selectedCorban.value) : undefined,
            promotoraId: selectedPromotora?.value !== undefined ? Number(selectedPromotora.value) : undefined,
            gerenteName: selectedGerente?.label || undefined,
             vendedorName: selectedVendedor?.label || undefined,
          }
        );
      } else if (filters.type === "kam") {
        result = await getKamPaginated(
          filters.startDate,
          filters.endDate,
          page,
          limit,
          vision || "all",
          {
            corbanId: selectedCorban?.value !== undefined ? Number(selectedCorban.value) : undefined,
            promotoraId: selectedPromotora?.value !== undefined ? Number(selectedPromotora.value) : undefined,
            gerenteName: selectedGerente?.label || undefined,
            vendedorName: selectedVendedor?.label || undefined,
          }
        );
      } else {
        result = await getCommissionsB2BPaginated(
          filters.startDate,
          filters.endDate,
          page,
          limit,
          [],
          vision,
          {
            corbanId: selectedCorban?.value !== undefined ? Number(selectedCorban.value) : undefined,
            promotoraId: selectedPromotora?.value !== undefined ? Number(selectedPromotora.value) : undefined,
            gerenteName: selectedGerente?.label || undefined,
            vendedorName: selectedVendedor?.label || undefined,
          }
        );

        // Ensure taxes are negative for display in B2B Kamino
        if (vision === "kamino") {
          result.items = result.items.map((item: any) => ({
            ...item,
            csll: item.csll ? Math.abs(item.csll) * -1 : 0,
            irrf: item.irrf ? Math.abs(item.irrf) * -1 : 0,
            pis: item.pis ? Math.abs(item.pis) * -1 : 0,
            cofins: item.cofins ? Math.abs(item.cofins) * -1 : 0,
          }));
        }
      }
      setData(result.items.slice(0, limit));
      setMeta({ totalPages: result.totalPages, currentPage: result.currentPage, itemsPerPage: limit });
      setHasQueried(true);
    } catch (error: any) {
      toast({ title: "Erro ao buscar dados", description: error.message, status: "error", duration: 5000 });
    } finally {
      setIsQuerying(false);
    }
  };

  const checkHasData = async (type: "b2c" | "b2b" | "kam"): Promise<boolean> => {
    if (!filters.startDate || !filters.endDate) {
      toast({ title: "Selecione o intervalo de datas", status: "warning", duration: 3000 });
      return false;
    }

    try {
      let result;
      if (type === "b2c") {
        result = await getCommissionsB2CPaginated(
          filters.startDate,
          filters.endDate,
          1,
          1,
          {
            corbanId: selectedCorban?.value ? Number(selectedCorban.value) : undefined,
            promotoraId: selectedPromotora?.value ? Number(selectedPromotora.value) : undefined,
            gerenteName: selectedGerente?.label || undefined,
            vendedorName: selectedVendedor?.label || undefined,
          }
        );
      } else if (type === "kam") {
        result = await getKamPaginated(
          filters.startDate,
          filters.endDate,
          1,
          1,
          vision || "all",
          {
            corbanId: selectedCorban?.value !== undefined ? Number(selectedCorban.value) : undefined,
            promotoraId: selectedPromotora?.value !== undefined ? Number(selectedPromotora.value) : undefined,
            gerenteName: selectedGerente?.label || undefined,
            vendedorName: selectedVendedor?.label || undefined,
          }
        );
      } else {
        result = await getCommissionsB2BPaginated(
          filters.startDate,
          filters.endDate,
          1,
          1,
          [],
          vision,
          {
            corbanId: selectedCorban?.value ? Number(selectedCorban.value) : undefined,
            promotoraId: selectedPromotora?.value ? Number(selectedPromotora.value) : undefined,
            gerenteName: selectedGerente?.label || undefined,
            vendedorName: selectedVendedor?.label || undefined,
          }
        );
      }

      if (!result?.totalItems || result.totalItems === 0) {
        toast({
          title: "Nenhum relatório encontrado para o intervalo de datas selecionado.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return false;
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleClearFilters = () => {
    setDateRange([null, null]);
    setFilters(INITIAL_FILTERS);
    setSelectedCorban(null);
    setSelectedPromotora(null);
    setSelectedGerente(null);
    setSelectedVendedor(null);
    setVision("");
    setData([]);
    setHasQueried(false);
  };

  const handleDownloadB2C = async () => {
    const hasData = await checkHasData("b2c");
    if (!hasData) return;

    setIsDownloadingB2C(true);
    try {
      await downloadCommissionsB2C(
        filters.startDate,
        filters.endDate,
        selectedCorban?.value ? [Number(selectedCorban.value)] : [],
        {
          promotoraId: selectedPromotora?.value ? Number(selectedPromotora.value) : undefined,
          gerenteName: selectedGerente?.label || undefined,
          vendedorName: selectedVendedor?.label || undefined,
        }
      );
      toast({ title: "Sucesso", description: "Relatório B2C gerado com sucesso", status: "success", duration: 5000 });
    } catch (error: any) {
      toast({ title: "Erro ao baixar relatório B2C", description: `${error}`, status: "error", duration: 5000 });
    } finally {
      setIsDownloadingB2C(false);
    }
  };

  const handleDownloadB2B = async () => {
    const hasData = await checkHasData("b2b");
    if (!hasData) return;

    setIsDownloadingB2B(true);
    try {
      await downloadCommissionsB2B(filters.startDate, filters.endDate, [], {
        corbanId: selectedCorban?.value ? Number(selectedCorban.value) : undefined,
        promotoraId: selectedPromotora?.value ? Number(selectedPromotora.value) : undefined,
        gerenteName: selectedGerente?.label || undefined,
        vendedorName: selectedVendedor?.label || undefined,
      });
      toast({ title: "Sucesso", description: "Relatório B2B gerado com sucesso", status: "success", duration: 5000 });
    } catch (error: any) {
      toast({ title: "Erro ao baixar relatório B2B", description: `${error}`, status: "error", duration: 5000 });
    } finally {
      setIsDownloadingB2B(false);
    }
  };

  const handleDownloadKam = async () => {
    const hasData = await checkHasData("kam");
    if (!hasData) return;

    setIsDownloadingKam(true);
    try {
      await downloadKamReport(filters.startDate, filters.endDate, vision || "all", {
        corbanId: selectedCorban?.value !== undefined ? Number(selectedCorban.value) : undefined,
        promotoraId: selectedPromotora?.value !== undefined ? Number(selectedPromotora.value) : undefined,
        gerenteName: selectedGerente?.label || undefined,
        vendedorName: selectedVendedor?.label || undefined,
      });
      toast({ title: "Sucesso", description: "Relatório KAM gerado com sucesso", status: "success", duration: 5000 });
    } catch (error: any) {
      toast({ title: "Erro ao baixar o relatório", description: `${error}`, status: "error", duration: 5000 });
    } finally {
      setIsDownloadingKam(false);
    }
  };

  const formatCell = useCallback((key: string, value: any): string => {
    if (value == null || value === "") return "-";
    if (DATE_KEYS.has(key)) return new Date(value).toLocaleDateString("pt-BR");
    if (CURRENCY_KEYS.has(key)) return typeof value === "number" ? BrlCurrencyFormatter(value) : "-";
    if (DOCUMENT_KEYS.has(key)) return cpfFormatter(value);
    if (PHONE_KEYS.has(key)) return formatPhone(value);
    if (key === "percentualComissao") return `${value}%`;
    return String(value);
  }, []);

  return (
    <Box width="100%" minW="0">
      <CommissionFilters
        backgroundStyle={backgroundStyle}
        dateRange={dateRange}
        onDateChange={handleDateChange}
        filters={filters}
        onFilterChange={setFilters}
        vision={vision}
        onVisionChange={setVision}
        selectedCorban={selectedCorban}
        onCorbanSelect={setSelectedCorban}
        selectedPromotora={selectedPromotora}
        onPromotoraSelect={setSelectedPromotora}
        selectedGerente={selectedGerente}
        onGerenteSelect={setSelectedGerente}
        selectedVendedor={selectedVendedor}
        onVendedorSelect={setSelectedVendedor}
        onQuery={() => handleQuery(1, pageSize)}
        onClear={handleClearFilters}
        onDownloadB2C={handleDownloadB2C}
        onDownloadB2B={handleDownloadB2B}
        onDownloadKam={handleDownloadKam}
        isQuerying={isQuerying}
        isDownloadingB2C={isDownloadingB2C}
        isDownloadingB2B={isDownloadingB2B}
        isDownloadingKam={isDownloadingKam}
        hasQueried={hasQueried}
        setHasQueried={setHasQueried}
        setData={setData}
      />

      <CommissionTable
        backgroundStyle={backgroundStyle}
        columns={currentColumns}
        data={data}
        isQuerying={isQuerying}
        hasQueried={hasQueried}
        meta={meta}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          handleQuery(1, size);
        }}
        onPageChange={(page) => handleQuery(page, pageSize)}
        onClearFilters={handleClearFilters}
        formatCell={formatCell}
      />
    </Box>
  );
}